import { unitFieldUtils } from '../../v4/shared/components/forms/input/unit-field-utils.js';
import { friendlyStringFromConst } from 'v1/helpers/misc';

/**
 * Generates caption for mapping source label. For custom fields (e.g., custom_9),
 * resolves the custom field definition name and shows it verbatim (preserves casing).
 * For snake_case keys (e.g. full_name), applies friendlyStringFromConst: first letter
 * upper, rest lower, underscores to spaces.
 *
 * @param {string} [mapsTo] - The maps_to value (e.g. 'full_name', 'custom_9')
 * @param {Object} [calloutFieldMapper] - Mapper from field key to { label, ... }
 * @returns {string}
 */
export function generateMappingSourceCaption(mapsTo, calloutFieldMapper) {
  if (!mapsTo) {
    return '';
  }
  if (mapsTo.includes('custom_')) {
    const customFieldInfo = calloutFieldMapper?.[mapsTo];
    if (customFieldInfo?.label) {
      return customFieldInfo.label;
    }
  }
  return friendlyStringFromConst(mapsTo);
}

const SOCIAL_FIELDS_MAPPING = {
  social_instagram: {
    label: 'Instagram',
    type: 'SOCIAL',
    maps_to: 'social_instagram',
    key: 'social_instagram'
  },
  social_twitter: {
    label: 'Twitter',
    type: 'SOCIAL',
    maps_to: 'social_twitter',
    key: 'social_twitter'
  },
  social_behance: {
    label: 'Behance',
    type: 'SOCIAL',
    maps_to: 'social_behance',
    key: 'social_behance'
  },
  social_youtube: {
    label: 'Youtube',
    type: 'SOCIAL',
    maps_to: 'social_youtube',
    key: 'social_youtube'
  },
  social_vimeo: {
    label: 'Vimeo',
    type: 'SOCIAL',
    maps_to: 'social_vimeo',
    key: 'social_vimeo'
  }
};

const CORE_FIELDS_MAPPING = {
  profile_picture: {
    label: 'Profile picture',
    maps_to: 'profile_picture',
    type: 'PROFILE_PICTURE',
    key: 'profile_picture'
  },
  full_name: {
    label: 'Full name',
    maps_to: 'full_name',
    type: 'SHORT_TEXT',
    key: 'full_name'
  },
  files: {
    label: 'Media uploader',
    type: 'FILES',
    maps_to: 'files',
    key: 'files'
  },
  role: {
    label: 'Role',
    maps_to: 'role',
    type: 'SHORT_TEXT',
    key: 'role'
  },
  websites: {
    label: 'Website',
    type: 'URL',
    maps_to: 'websites',
    key: 'websites'
  },
  emails: {
    label: 'Email',
    type: 'EMAIL',
    maps_to: 'emails',
    key: 'emails'
  },
  phone_numbers: {
    label: 'Phone number',
    type: 'PHONE_NUMBER',
    maps_to: 'phone_numbers',
    key: 'phone_numbers'
  },
  locations: {
    label: 'Location',
    type: 'LOCATION',
    maps_to: 'locations',
    key: 'locations'
  },
  rates: {
    label: 'Rate',
    type: 'CURRENCY',
    maps_to: 'rates',
    key: 'rates'
  },
  tags: {
    label: 'Tags',
    type: 'MULTI_SELECT',
    maps_to: 'tags',
    key: 'tags'
  },
  organisation_id: {
    label: 'Organisation',
    type: 'SHORT_TEXT',
    maps_to: 'organisation_id',
    key: 'organisation_id'
  },
  groups: {
    label: 'Groups',
    type: 'SHORT_TEXT',
    maps_to: 'groups',
    key: 'groups'
  }
};

/**
 * @function generateCalloutFieldMapper
 * Creates an object used throughout callouts to list the resource fields and provide the correct field data necessary to build the form.
 * This uses CORE_FIELDS_MAPPINGS to grab correct field data related to the selected resources fields and custom_field_definitions to get
 * the appropriate custom field data and generated appropriate field objects for those custom fields.
 *
 * @param {Array} fields - The metastructure fields found within a selected resource_type
 * @param {Array} custom_field_definitions - The list of availability custom fields on the system to grab custom field data for custom fields found within the resource type
 * @param {Object} i18n - The i18n object used to translate the field labels
 */
export const generateCalloutFieldMapper = (
  fields = [],
  custom_field_definitions,
  i18n
) => {
  const fieldMappings = fields.reduce((result, field) => {
    let map;
    let key;
    if (field.type === 'CUSTOM_FIELD') {
      const definition =
        custom_field_definitions.find(
          d => d.id === field.custom_field_definition_id
        ) ?? {};
      key = `custom_${definition.id}`;
      map = {
        label: i18n.t(`callouts:fieldMappings.${key}`, {
          defaultValue: definition.name
        }),
        type: definition.data_type,
        maps_to: key,
        options: definition.options || null,
        key
      };
      if (definition.data_type === 'MEASUREMENT_NEW') {
        map.default_value = unitFieldUtils.getUnitValueFromDisplayValue(
          0,
          definition.measurement_default_unit ?? 'ft'
        );
      }
    } else {
      if (field.name === 'socials') {
        const translatedSocials = Object.keys(SOCIAL_FIELDS_MAPPING).reduce(
          (acc, socialKey) => {
            acc[socialKey] = {
              ...SOCIAL_FIELDS_MAPPING[socialKey],
              label: i18n.t(`callouts:fieldMappings.${socialKey}`, {
                defaultValue: SOCIAL_FIELDS_MAPPING[socialKey].label
              })
            };
            return acc;
          },
          {}
        );
        return { ...result, ...translatedSocials };
      } else {
        key = field.name;
        map = { ...CORE_FIELDS_MAPPING[field.name] };
        map.label = i18n.t(`core:core_fields.${key}`, {
          defaultValue: map.label
        });
      }
    }
    // TODO: Handle custom field groups.
    return { ...result, [key]: map };
  }, {});

  return {
    ...fieldMappings,
    files: CORE_FIELDS_MAPPING['files'],
    media: CORE_FIELDS_MAPPING['media']
  };
};
